import {useFloating} from '@floating-ui/react';
import { format} from 'date-fns';
import FocusTrap from 'focus-trap-react';
import React, {useRef, useState} from 'react';
import { DateRange, DayPicker } from 'react-day-picker';

import { FormatTypes } from '../../../types/formatTypes';

export default function DateRangePicker(props: Props) {
    const [range, setRange] = useState<DateRange|undefined>({from: props.defaultFromDate, to: props.defaultToDate});
    const [isPopperOpen, setIsPopperOpen] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const {
            refs,
            floatingStyles
        } = useFloating({
            placement: 'bottom-start',
        });

    const closePopper = () => {
        setIsPopperOpen(false);
        buttonRef?.current?.focus();
    };

    const handleButtonClick = () => {
        setIsPopperOpen(true);
    };

    const handleRangeSelect = (range: DateRange | undefined)  => {
        setRange(range);

        if(!range?.from || !range?.to) {
            props.onDateRangeChange(undefined);
            return;
        }

        if(range?.from && range?.to) {
            props.onDateRangeChange(range);
            closePopper();
        }
    };

    const getFormattedValue = () => {
        if(!range?.from || !range?.to) return '';
        if(range.from && !range.to){
            return format(range.from, FormatTypes.DATE_FORMAT);
        }

        if(!range.from && range.to){
            return ` - ${format(range.to, FormatTypes.DATE_FORMAT)}`;
        }

        return `${format(range.from as Date, FormatTypes.DATE_FORMAT)} - ${format(range.to as Date, FormatTypes.DATE_FORMAT)}`;
    };

    return (
        <div className="date-range-picker">
            <div ref={refs.setReference} className="date-range-picker-input-container">
                <input type="text"
                    className="date-range-picker-input-container-input"
                    value={getFormattedValue()}
                    readOnly
                />
                <button className="date-range-picker-input-container-button"
                    ref={buttonRef}
                    onClick={handleButtonClick}>
                     <i className="fas fa-calendar-alt"/>
                </button>
            </div>
            {isPopperOpen && (
                <FocusTrap
                    active
                    focusTrapOptions={{
                        initialFocus: false,
                        allowOutsideClick: true,
                        clickOutsideDeactivates: true,
                        onDeactivate: closePopper,
                        fallbackFocus: buttonRef.current as HTMLButtonElement
                }}>
                    <div
                        tabIndex={-1}
                        style={floatingStyles}
                        className="dialog-sheet"
                        ref={refs.setFloating}
                        role="dialog"
                    >
                        <DayPicker
                            className="date-range-picker__day-picker"
                            autoFocus={isPopperOpen}
                            mode="range"
                            showWeekNumber
                            weekStartsOn={1}
                            numberOfMonths={props.numberOfMonths}
                            defaultMonth={props.defaultFromDate}
                            startMonth={props.minDate}
                            endMonth={props.maxDate}

                            selected={range}
                            onSelect={handleRangeSelect}
                        />
                    </div>
                </FocusTrap>)
                }
        </div>);
}

type Props = {
    minDate: Date,
    maxDate: Date,
    defaultFromDate: Date,
    defaultToDate: Date,
    numberOfMonths: number,
    onDateRangeChange: (dateRange: DateRange| undefined) => void
}
